import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoService from "@/services/investments/sacco/SaccoService.ts";

/**
 * Hook to update an existing sacco deposit through the update_sacco_deposit_with_debit RPC.
 *
 * A deposit linked to a debit transaction has that transaction re-priced and the source account
 * balance adjusted in the same database transaction; a legacy deposit has only its row updated.
 *
 * On success: invalidate ['transactions'], ['accounts'] and ['sacco_deposits', membershipId].
 */
export default function useUpdateSaccoDepositWithDebit() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: SaccoDepositWithDebitUpdateRequest) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      return await saccoService.updateDepositWithDebit(user.id, input);
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['sacco_deposits', input.membershipId] });
    },
    onError: (error: Error) => {
      console.error('Sacco deposit update failed:', error);
    },
  });
}
